import { redirect } from '@remix-run/cloudflare';
import type { LoaderFunctionArgs } from '@remix-run/cloudflare';
import type {
  CourseAssessment,
  LiftAssessment,
  LiftCourseAssessmentsResponse,
} from '@tbd/simcapture-api-types';
import type { SimCaptureApiError } from '#app/simcapture/index.js';
import { createMinimalClient } from '#app/simcapture/index.js';
import invariant from '#app/tiny-invariant.js';
import {
  isEvaluationCompleted,
  isEvaluationInProgress,
  isScenario,
} from '#app/util/assessments.js';

const getAssessmentPath = (courseId: string, a?: LiftAssessment) => {
  const root = `/participant/sessions/${courseId}/assessments`;
  if (!a) return root;

  let subpath = '';
  if (isScenario(a) && isEvaluationCompleted(a))
    subpath = `/${a.evaluationId}/result?scenarioId=${a.scenarioId}&evaluationTemplateId=${a.evaluationTemplateId}`;
  else if (isEvaluationInProgress(a)) subpath = `/${a.evaluationId}`;
  else if (isEvaluationCompleted(a)) subpath = `/${a.evaluationId}/result`;

  return `${root}${subpath}`;
};

export const loader = async ({
  context,
  request,
  params,
}: LoaderFunctionArgs) => {
  const { simcapture, user } = context;
  const url = new URL(request.url);
  invariant(params.courseId, 'missing courseId');
  const evaluationTemplateId = url.searchParams.get('evaluationTemplateId');

  const errorPath =
    getAssessmentPath(params.courseId) +
    `?msg=${'Something went wrong. Evaluation could not be opened.'}`;

  // we don't want the typical logout on 403 behavior here
  const minimal = createMinimalClient({ request, user });

  // try starting the evaluation
  const result: CourseAssessment | SimCaptureApiError = await minimal.post(
    `/evaluations/evaluation-templates/${evaluationTemplateId}/course`,
    {},
    { label: 'create_evaluation' }
  );

  // if there is an error
  if ('error' in result || 'errorCode' in result) {
    // this endpoint 403's if an evalaution is already in progress
    if ('statusCode' in result && result.statusCode === 403) {
      // perhaps there is already an evaluation?
      const { courseEvaluations }: LiftCourseAssessmentsResponse =
        await simcapture.get(
          `/lift/participant/courses/${params.courseId}/assessments`,
          {
            label: 'participant_course_assessments',
          }
        );
      const existingEvaluation = courseEvaluations
        .filter((e) => e.evaluationTemplateId === evaluationTemplateId)
        ?.pop();

      // if there is an existing evaluation, redirect to it
      if (existingEvaluation) {
        throw redirect(getAssessmentPath(params.courseId, existingEvaluation));
      }
    }

    // got 403 (but no existing evaluation) or some other error
    throw redirect(errorPath);
  }

  // no errors, send to new evaluation
  throw redirect(getAssessmentPath(params.courseId, result));
};

export default () => <div>Redirecting...</div>;
